import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  galleryImages?: string[];
  category: string;
  rating: number;
  reviews: number;
  badge?: string;
  description: string;
  sizes?: string[];
}

/** Fetch all active products from the database and normalize to the Product shape. */
export function useProducts() {
  return useQuery({
    queryKey: ["storefront-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map(normalizeProduct);
    },
    staleTime: 1000 * 60 * 2, // 2 min cache
  });
}

/** Fetch a single product by ID. */
export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: ["storefront-product", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data ? normalizeProduct(data) : null;
    },
    enabled: !!id,
  });
}

function normalizeProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    price: Number(row.price),
    originalPrice: row.original_price ? Number(row.original_price) : undefined,
    image: row.image_url,
    galleryImages: row.gallery_images || [],
    category: row.category,
    rating: Number(row.rating),
    reviews: row.reviews_count,
    badge: row.badge || undefined,
    description: row.description,
    sizes: row.sizes || [],
  };
}

/** Extract unique categories from products. */
export function useCategories() {
  const { data: products } = useProducts();
  const cats = products ? ["All", ...Array.from(new Set(products.map((p) => p.category)))] : ["All"];
  return cats;
}
